import {Location} from '@angular/common'
import {Component, OnInit} from '@angular/core';
import {UntypedFormGroup, UntypedFormBuilder, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {Title} from '@angular/platform-browser';
import {BehaviorSubject, throwError} from 'rxjs';
import {catchError, shareReplay, map, tap, switchMap} from 'rxjs/operators';

import {UserService} from 'src/app/services/shared/user.service';
import {TwoFactorModel} from '../my-account/model/TwoFactorModel.model';
import {AgentDetailsResponse} from '../my-account/model/agentDetailsResponse.model';
import {TerraUtils} from 'src/app/shared/TerraUtils';
import {AppSettingsService, SubdomainEnum} from 'src/app/services/app-settings.service';
import {RoutingService} from 'src/app/services/routing.service';

@Component({
  selector: 'terra-two-factor-verification',
  templateUrl: './two-factor-verification.component.html',
  styleUrls: ['./two-factor-verification.component.scss']
})
export class TwoFactorVerificationComponent implements OnInit {
  paths = TerraUtils.consts.paths;
  DEFAULT_GP_LOGGED_IN_INDEX = this.paths.DASHBOARD;
  DEFAULT_LP_LOGGED_IN_INDEX = this.routingService.lpHomePage;

  pageForm: UntypedFormGroup;
  isSubmitted = false;
  formSubmitting = false;

  isGeneralServerError = false;
  generalServerErrorMessage = TerraUtils.consts.messages.GENERAL_SUBMIT_ERROR;


  isCodeSent$ = this.userService.sendTwoFactorCode().pipe(
    map(() => {
      return true;
    }),
    shareReplay(1),
    catchError(error => {
      this.isGeneralServerError = true;
      return throwError(error);
    }));

  phoneMasked$ = this.userService.accountDetails$
    .pipe(
      map(accountDetails => {
        return '*** ***' + accountDetails.phone1.substring(accountDetails.phone1.length - 3);
      }));

  resendingCodeInProgress$ = new BehaviorSubject<boolean>(false);

  constructor(
    private userService: UserService,
    private fb: UntypedFormBuilder,
    private router: Router,
    private titleService: Title,
    private appSettingsService: AppSettingsService,
    private routingService: RoutingService,
    private route: ActivatedRoute,
    private location: Location
  ) {
  }

  ngOnInit() {
    this.titleService.setTitle('Two-Factor Authentication');

    this.pageForm = this.fb.group({
      twoFactorCode: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(6)]],
      saveDevice: [false]
    });
  }

  resendCode() {
    this.isGeneralServerError = false;
    if (this.resendingCodeInProgress$.value === true) {
      return;
    }

    this.resendingCodeInProgress$.next(true);
    this.userService
      .resendTwoFactorCode()
      .pipe(
        tap(() => this.resendingCodeInProgress$.next(false)),
        catchError(error => {
          this.resendingCodeInProgress$.next(false);
          this.isGeneralServerError = true;
          return throwError(error);
        }))
      .subscribe();
  }

  generateSubmitModel(): TwoFactorModel {
    const formValues = this.pageForm.value;
    const model = new TwoFactorModel();
    model.code = formValues.twoFactorCode;
    model.rememberDevice = formValues.saveDevice;
    return model;
  }

  submit() {

    if (!this.pageForm.valid) {
      this.pageForm.updateValueAndValidity();
      return;
    }

    this.isSubmitted = true;
    this.isGeneralServerError = false;

    const model = this.generateSubmitModel();

    this.formSubmitting = true;

    this.userService
      .twoFactorVerify(model)
      .pipe(
        switchMap(() => this.userService.accountDetails$)
      )
      .subscribe(
        accountDetails => {
          this.formSubmitting = false;
          this.redirect(accountDetails);
        },
        error => {
          if (error.status === 400) {
            const codeFormControl = this.pageForm.get('twoFactorCode');
            codeFormControl.setErrors({codeMismatch: true});
          } else {
            this.isGeneralServerError = true;
          }
          this.formSubmitting = false;
        }
      );
  }

  redirect(clientDetails: AgentDetailsResponse) {
    const isGp = this.appSettingsService.subDomain === SubdomainEnum.GP;
    if (isGp && !clientDetails.isClientFilledPersonalDetails) {
      this.routingService.goToReturnUrl(this.paths.ONBOARDING)
      return;
    }
    this.routingService.goToReturnUrl(this.routingService.defaultLoggedInPageBySubdomain)
  }
}
